/**
 * RoutineChannelPicker
 *
 * Controlled per-routine alert-channel picker: a Slack channel picker and a
 * Google Chat space picker. The in-app card is always created (it's the alert
 * record and the "continue in chat" deep-link target), so it's not toggleable
 * here — the picker only chooses whether to ALSO deliver to Slack / Google Chat.
 * Both provider sections render the SAME `ProviderPicker` presentational
 * component, so the chips / Slack-invite / saved-but-missing (stale) row
 * behaviour is shared, not duplicated. All of that derivation lives in one
 * place — `useChannelPickerState` — which returns ready-to-spread props for the
 * two `ProviderPicker`s.
 *
 * Each routine picks its own Slack/Google Chat destinations; when it pins none,
 * the parent can pass a `tenantDefault` summary so the section reads as "Using
 * default: …" instead of looking empty.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, UserPlus, CheckCircle } from 'lucide-react';
import { cn, toast } from '@strivelabs/ui-core';
import { Button } from '@strivelabs/ui-core/v2';
import { useCredentials } from '@/hooks/useConnections';
import { useDestinationList } from '@/hooks/useDestinationList';
import { executeAppAction, BASE_URL } from '@/services/DataService';
import type { ConnectionsCardData } from '@/types/connections';
import type { RoutineChannelValue } from '@/utils/routineOutputChannels';
import {
  ProviderPicker,
  staleBadge,
  type PickerRow,
  type SelectedChip,
  type ProviderPickerProps,
} from './ProviderPicker';
import {
  extractChannels,
  extractSpaces,
  type SlackChannel,
  type GoogleChatSpace,
} from './channelData';

export type ProviderSectionProps = Omit<ProviderPickerProps, 'displayName' | 'noun' | 'helper'>;

export interface ChannelPickerState {
  /** Ready-to-spread props for the Slack `ProviderPicker`. */
  slackProps: ProviderSectionProps;
  /** Ready-to-spread props for the Google Chat `ProviderPicker`. */
  gchatProps: ProviderSectionProps;
}

/**
 * All fetch / credential-resolution / invite / stale-row derivation for both
 * providers, driven by the controlled `value` and reported back through
 * `onChange`. Returns the props each `ProviderPicker` needs, so the picker
 * component itself stays declarative and the invite/stale logic exists ONCE.
 * Exported for the in-chat `ui_channel_input` card, which renders its own
 * checkbox layout over the same rows/chips/invite plumbing.
 */
export function useChannelPickerState(
  value: RoutineChannelValue,
  onChange: (next: RoutineChannelValue) => void
): ChannelPickerState {
  const navigate = useNavigate();
  const location = useLocation();

  // Stable identity for the selected-id arrays so the toggle/invite callbacks
  // below don't get a fresh dependency on every render.
  const slackChannelIds = useMemo(() => value.slack?.channels ?? [], [value.slack?.channels]);
  const gchatSpaceNames = useMemo(() => value.gchat?.spaces ?? [], [value.gchat?.spaces]);

  const { data: credentialsData } = useCredentials();
  const creds = useMemo(
    () => (credentialsData?.credentials ?? []) as ConnectionsCardData[],
    [credentialsData]
  );

  // Effective credential per provider: the one pinned in `value`, else the
  // first connected account (lazy — only persisted into `value` when a
  // destination is selected, so opening the editor never dirties the routine).
  const slackCredId = useMemo(
    () => value.slack?.credential_id || creds.find((c) => c.app_name === 'slack')?.id || '',
    [value.slack?.credential_id, creds]
  );
  const gchatCredId = useMemo(
    () => value.gchat?.credential_id || creds.find((c) => c.app_name === 'google_chat')?.id || '',
    [value.gchat?.credential_id, creds]
  );
  const credsById = useMemo(() => new Map(creds.map((c) => [c.id, c])), [creds]);
  const slackCred = credsById.get(slackCredId);
  const gchatCred = credsById.get(gchatCredId);

  const { items: channels, isLoading: isLoadingChannels, setItems: setChannels } =
    useDestinationList<SlackChannel>(
      slackCredId, 'slack', 'list_conversations', extractChannels, 'Slack channels'
    );
  const { items: spaces, isLoading: isLoadingSpaces } =
    useDestinationList<GoogleChatSpace>(
      gchatCredId, 'google_chat', 'GOOGLE_CHAT_LIST_SPACES', extractSpaces, 'Google Chat spaces'
    );

  const [invitingChannels, setInvitingChannels] = useState<Record<string, boolean>>({});

  const channelById = useMemo(() => new Map(channels.map((c) => [c.id, c])), [channels]);
  const spaceByName = useMemo(() => new Map(spaces.map((s) => [s.name, s])), [spaces]);

  // Toggles rewrite the controlled `value` (never the fetched list), so toggling
  // one destination can't drop a saved-but-unfetched sibling. The provider's
  // credential is pinned alongside the selection so an edit is self-contained.
  const setSlack = useCallback(
    (nextChannels: string[]) => {
      onChange({ ...value, slack: { credential_id: slackCredId, channels: nextChannels } });
    },
    [onChange, value, slackCredId]
  );
  const setGchat = useCallback(
    (nextSpaces: string[]) => {
      onChange({ ...value, gchat: { credential_id: gchatCredId, spaces: nextSpaces } });
    },
    [onChange, value, gchatCredId]
  );

  const toggleSlack = useCallback(
    (id: string) => {
      setSlack(
        slackChannelIds.includes(id)
          ? slackChannelIds.filter((x) => x !== id)
          : [...slackChannelIds, id]
      );
    },
    [slackChannelIds, setSlack]
  );
  const toggleGchat = useCallback(
    (name: string) => {
      setGchat(
        gchatSpaceNames.includes(name)
          ? gchatSpaceNames.filter((x) => x !== name)
          : [...gchatSpaceNames, name]
      );
    },
    [gchatSpaceNames, setGchat]
  );

  const handleInvite = useCallback(
    async (channelId: string, channelName: string) => {
      if (!slackCredId) return;
      setInvitingChannels((prev) => ({ ...prev, [channelId]: true }));
      try {
        const response = (await executeAppAction(
          BASE_URL,
          'slack',
          'join_conversations',
          [{ id: slackCredId }],
          [{ channels: [channelId] }]
        )) as { status?: string; error?: { message?: string } };
        if (response.status !== 'success') {
          throw new Error(response?.error?.message || 'Slack join failed');
        }
        setChannels((prev) =>
          prev.map((ch) => (ch.id === channelId ? { ...ch, is_member: true } : ch))
        );
        // Auto-add the newly joined channel to the selection.
        if (!slackChannelIds.includes(channelId)) {
          setSlack([...slackChannelIds, channelId]);
        }
        toast({
          title: 'Joined Channel',
          description: `Joined #${channelName} successfully.`,
          variant: 'success',
        });
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Invite Failed',
          description: error instanceof Error ? error.message : 'Could not join the channel.',
        });
      } finally {
        setInvitingChannels((prev) => {
          const clone = { ...prev };
          delete clone[channelId];
          return clone;
        });
      }
    },
    [slackCredId, slackChannelIds, setSlack, setChannels]
  );

  const handleConnect = useCallback(
    (appName: 'slack' | 'google_chat') => {
      navigate('/ui/settings', {
        state: {
          openConnectionsModal: true,
          returnToRoute: location.pathname,
          connectionAppName: appName,
        },
      });
    },
    [navigate, location.pathname]
  );

  const renderInvite = useCallback(
    (channel: SlackChannel): ReactNode => {
      if (channel.is_member) {
        return (
          <Button variant="secondary" size="xs" icon={<CheckCircle />} disabled>
            Invited
          </Button>
        );
      }
      if (invitingChannels[channel.id]) {
        return (
          <Button variant="secondary" size="xs" icon={<Loader2 className="animate-spin" />} disabled>
            Inviting...
          </Button>
        );
      }
      return (
        <Button
          variant="secondary"
          size="xs"
          icon={<UserPlus />}
          onClick={(e) => {
            e.stopPropagation();
            handleInvite(channel.id, channel.name);
          }}
        >
          Invite
        </Button>
      );
    },
    [invitingChannels, handleInvite]
  );

  // Slack rows: fetched channels (members first, then alpha) + saved-but-missing
  // rows kept visible & removable. Saved-but-missing rows are suppressed while
  // the fetch is in flight, where "missing" only means "not loaded yet".
  const slackRows: PickerRow[] = [
    ...[...channels]
      .sort((a, b) => {
        if (a.is_member && !b.is_member) return -1;
        if (!a.is_member && b.is_member) return 1;
        return a.name.localeCompare(b.name);
      })
      .map((ch) => ({
        value: ch.id,
        label: `# ${ch.name}`,
        selectable: ch.is_member,
        trailing: renderInvite(ch),
      })),
    ...(isLoadingChannels
      ? []
      : slackChannelIds
          .filter((id) => !channelById.has(id))
          .map((id) => ({
            value: id,
            label: `# ${id}`,
            selectable: true,
            trailing: staleBadge('membership changed'),
          }))),
  ];

  const gchatRows: PickerRow[] = [
    ...[...spaces]
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map((s) => ({
        value: s.name,
        label: s.displayName,
        selectable: true,
        trailing:
          s.spaceType && s.spaceType !== 'SPACE' ? (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
              {s.spaceType.replace('_', ' ')}
            </span>
          ) : undefined,
      })),
    ...(isLoadingSpaces
      ? []
      : gchatSpaceNames
          .filter((name) => !spaceByName.has(name))
          .map((name) => ({
            value: name,
            label: name,
            selectable: true,
            trailing: staleBadge('not in list'),
          }))),
  ];

  // Selected chips per provider, label resolved from the fetched list; while it's
  // still loading and a saved id isn't resolved yet, flag it loading so the chip
  // shimmers instead of flashing a raw id.
  const slackSelected: SelectedChip[] = slackChannelIds.map((id) => {
    const channel = channelById.get(id);
    return channel
      ? { value: id, label: `# ${channel.name}`, loading: false }
      : { value: id, label: `# ${id}`, loading: isLoadingChannels };
  });
  const gchatSelected: SelectedChip[] = gchatSpaceNames.map((name) => {
    const space = spaceByName.get(name);
    return space
      ? { value: name, label: space.displayName, loading: false }
      : { value: name, label: name, loading: isLoadingSpaces };
  });

  return {
    slackProps: {
      appName: 'slack',
      account: slackCred,
      connected: Boolean(slackCredId),
      isLoading: isLoadingChannels,
      rows: slackRows,
      selected: slackSelected,
      onToggle: toggleSlack,
      onConnect: () => handleConnect('slack'),
    },
    gchatProps: {
      appName: 'google_chat',
      account: gchatCred,
      connected: Boolean(gchatCredId),
      isLoading: isLoadingSpaces,
      rows: gchatRows,
      selected: gchatSelected,
      onToggle: toggleGchat,
      onConnect: () => handleConnect('google_chat'),
    },
  };
}

export interface RoutineChannelPickerProps {
  value: RoutineChannelValue;
  onChange: (next: RoutineChannelValue) => void;
  /** `inline` tightens spacing for the inline editor. */
  variant?: 'card' | 'inline';
}

export const RoutineChannelPicker = ({
  value,
  onChange,
  variant = 'card',
}: RoutineChannelPickerProps) => {
  const { slackProps, gchatProps } = useChannelPickerState(value, onChange);

  return (
    <div className={cn(variant === 'inline' ? 'space-y-3' : 'space-y-4')}>
      <p className="text-xs text-gray-500">
        Alerts always appear in your in-app dashboard. Choose Slack channels and/or
        Google Chat spaces below to also deliver them there.
      </p>

      <ProviderPicker
        {...slackProps}
        displayName="Slack"
        noun="channels"
        helper={
          <p className="text-xs text-gray-500">
            Channels the bot hasn&apos;t joined can&apos;t be selected — use Invite to enable them.
          </p>
        }
      />

      <ProviderPicker
        {...gchatProps}
        displayName="Google Chat"
        noun="spaces"
        helper={
          <p className="text-xs text-gray-500">
            Spaces use your account membership. If a space isn&apos;t listed, add the StriveLabs
            bot to it in Google Chat and refresh.
          </p>
        }
      />
    </div>
  );
};
